import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { SubTenantService } from '../services/subtenant-service';
import { AuthService } from '../services/auth-service';
import { UserService } from '../services/user-service';
import { ERROR_REASON_NOT_ENABLED } from '../models/auth.model';
import { isAdminGroup } from '../models/user.model';

export const subtenantGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
    const subTenantService = inject(SubTenantService);
    const authService = inject(AuthService);
    const userService = inject(UserService);
    const router = inject(Router);

    const resolveNoSubtenantAccess = (): Observable<boolean | UrlTree> =>
        userService.getUser().pipe(
            take(1),
            map(user => isAdminGroup(user?.group)),
            catchError(() => of(isAdminGroup(authService.user()?.group))),
            map(isAdmin => {
                if (!isAdmin) {
                    return router.createUrlTree(['/error-page'], {
                        queryParams: { reason: ERROR_REASON_NOT_ENABLED },
                    });
                }
                subTenantService.markNoSubtenants();
                return true;
            })
        );

    if (!subTenantService.subtenantToken()) {
        return from(authService.ensureUserAlignment()).pipe(
            catchError(() => of(true)),
            switchMap(() => subTenantService.getSubtenants()),
            switchMap(subtenants => {
                if (!subtenants || subtenants.length === 0) {
                    return resolveNoSubtenantAccess();
                }
                subTenantService.setAvailableSubtenants(subtenants);
                subTenantService.hasSubtenants.set(true);

                if (subtenants.length === 1) {
                    const only = subtenants[0];
                    return subTenantService.selectSubtenant(only.id).pipe(
                        map(() => {
                            subTenantService.setSelectedSubtenantName(only.name);
                            return true as boolean | UrlTree;
                        }),
                        catchError(() =>
                            of<boolean | UrlTree>(router.createUrlTree(['/select-subtenant']))
                        )
                    );
                }

                return of<boolean | UrlTree>(router.createUrlTree(['/select-subtenant']));
            }),
            catchError(() => {
                subTenantService.markNoSubtenants();
                return of<boolean | UrlTree>(true);
            })
        );
    }

    return of(true);
};
